import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";
import type { Primitive } from "ts-essentials";

import { GIT_COMMIT_HASH, IS_APP, IS_DEV } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { StackTrace } from "@/app/ErrorBoundary.style.jsx";
import { Container } from "@/app/Missing.jsx";
import { NetworkError } from "@/util/custom-errors.mjs";
import { devError, devWarn } from "@/util/dev.mjs";
import type { ErrorDetail } from "@/util/error-sourcemap.mjs";
import sourceMap from "@/util/error-sourcemap.mjs";
import globals from "@/util/global-whitelist.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";
import { useTransientRoute } from "@/util/router-hooks.mjs";

export const EVENT_ERROR = "EVENT_ERROR";

export interface AppErrorEvent {
  error: Error | Primitive | object | unknown;
  mappedOutput?: string[];
  tags: string[];
  route?: string;
  errorData?: never;

  [key: string]: Primitive | object | unknown;
}

declare module "@/app/app-event-bus.mjs" {
  interface EventBusEvents {
    [EVENT_ERROR](error: AppErrorEvent): void;
  }
}

const GlobalWrapper = styled("div")`
  height: 100vh;
  background: var(--app-bg);
  color: var(--shade0);
  font-family: Inter;
`;

type ErrorProps = {
  error: Error;
} & (
  | { mappedOutput: string[]; detailsOutput: ErrorDetail[] }
  | { mappedOutput?: never; detailsOutput?: never }
);

export function DisplayError({
  error,
  mappedOutput,
  detailsOutput,
}: ErrorProps) {
  const route = useTransientRoute();
  const isOverlayRoute = isRouteOverlay(route.currentPath);

  const reload = useCallback(() => {
    globals.location.reload();
  }, []);

  const isNetworkError = error instanceof NetworkError;

  const { t } = useTranslation();

  if (!IS_DEV && isOverlayRoute) return null;

  return (
    <Container>
      <div>
        <div>{error.name}</div>
        <div>{error.message}</div>
        <StackTrace>
          {mappedOutput
            ? IS_DEV
              ? detailsOutput.map(({ callee, path }, i) => {
                  const vscodeLink = `vscode://file/${__CODE_BASE_DIR__}src/${path}`;
                  return (
                    <span key={i}>
                      {callee ? `${callee} - ` : ""}
                      <a href={vscodeLink}>{path}</a>
                    </span>
                  );
                })
              : mappedOutput.join("\n")
            : error.stack}
        </StackTrace>
        {/* eslint-disable i18next/no-literal-string */}
        {!IS_DEV ? (
          IS_APP ? (
            <pre>v{mainRefs.PKG_VERSION}</pre>
          ) : (
            <pre>{GIT_COMMIT_HASH}</pre>
          )
        ) : null}
        {/* eslint-enable i18next/no-literal-string */}
        {isNetworkError ? (
          <Button onClick={reload}>
            {t("common:client.reload", "Reload")}
          </Button>
        ) : null}
      </div>
    </Container>
  );
}

type ErrorBoundaryProps = React.PropsWithChildren<{
  routeComponent?: () => JSX.Element;

  tags?: string[];
  isGlobal?: boolean;
}>;

interface ErrorBoundaryState extends Omit<Partial<ErrorProps>, "error"> {
  routeComponent?: () => JSX.Element;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { error: null };
  }

  // Reset error state when the route changes.
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): ErrorBoundaryState {
    const { routeComponent } = props;
    const { routeComponent: previousRouteComponent } = state;

    const derivedState = { ...state, routeComponent };

    if (routeComponent !== previousRouteComponent) {
      derivedState.error = null;
    }

    return derivedState;
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    if (error instanceof Error) {
      return { error };
    }

    return { error: new Error("An unknown error occurred.", { cause: error }) };
  }

  override async componentDidCatch(caughtError: unknown) {
    try {
      let error: Error;

      if (!(caughtError instanceof Error)) {
        devWarn(
          "[ErrorBoundary] Caught an error that was not of type Error",
          caughtError,
        );

        error = new Error("An unknown error occurred.", { cause: caughtError });
      } else {
        error = caughtError;

        devError("[ErrorBoundary]", caughtError);
      }

      const [mappedOutput, detailsOutput] = await sourceMap(error);

      this.setState({ error, mappedOutput, detailsOutput });

      if (!IS_DEV) {
        eventBus.emit(EVENT_ERROR, {
          error,
          mappedOutput,
          tags: this.props.tags ?? ["error_boundary"],
          route: router?.route?.path ? stringifyPath(router?.route?.path) : "",
          routeParams: router?.route?.parameters,
        });
      }
    } catch (e) {
      // Should actually just completely swallow this error, but only for devs.
      if (!IS_DEV) {
        devError("Failed to sourcemap from ErrorBoundary!", e);
      }
    }
  }

  override render() {
    const { state, props } = this;

    const Wrapper = props.isGlobal ? GlobalWrapper : React.Fragment;

    if (state.error) {
      // If error is populated, mappedOutput and detailsOutput should be too
      // so we can cast to ErrorProps.
      return (
        <Wrapper>
          <DisplayError {...(state as ErrorProps)} />
        </Wrapper>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
